import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "util/context/user_context";
import {
  getNotificationsApi,
  checkAllNotificationsApi,
} from "api/notification";
import {
  notificationTarget,
  HIDDEN_NOTIF_TYPES,
} from "components/header/notification_bell";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  bg: "#F7FAF7",
  red: "#EF4444",
};

const FONT =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif';

function formatDateTime(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
}

function categoryText(notif) {
  try {
    const arr = JSON.parse(notif.categoryListName || "[]");
    if (Array.isArray(arr) && arr.length) return arr.filter(Boolean).join(" · ");
  } catch {}
  return "";
}

function NotifItem({ notif, onClick }) {
  const isMatch = notif.type === "수리";
  const isComment = notif.type === "댓글";
  const isCommunity = notif.type === "작성";
  const unread = !notif.checkDate || notif.checkDate === "";
  const cat = categoryText(notif);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "16px 18px",
        border: `1px solid ${C.line}`,
        background: unread ? C.greenBg : "#fff",
        borderRadius: 14,
        cursor: "pointer",
        display: "flex",
        gap: 14,
        alignItems: "flex-start",
        transition: "background 0.12s ease",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 9999,
          background: isMatch ? "#FFF7ED" : isComment ? C.greenBg : "#EFF6FF",
          color: isMatch ? "#C2410C" : isComment ? C.greenDark : "#1D4ED8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          fontSize: 20,
        }}
        aria-hidden
      >
        {isMatch ? "🔧" : isComment ? "💬" : isCommunity ? "📝" : "🔔"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 4,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.01em",
            }}
          >
            {isMatch
              ? `${cat || "새 수리요청"} 알림`
              : isComment
              ? `${cat || "새"} 댓글`
              : isCommunity
              ? "커뮤니티 새 글"
              : "알림"}
          </div>
          {unread && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 800,
                color: "#fff",
                background: C.red,
                padding: "2px 8px",
                borderRadius: 9999,
              }}
            >
              NEW
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 13,
            color: C.body,
            lineHeight: 1.55,
            wordBreak: "break-word",
          }}
        >
          {isMatch
            ? notif.address || "수리 요청이 도착했어요. 상세 화면에서 확인해주세요."
            : isComment
            ? `${notif.communityTitle ? `[${notif.communityTitle}] ` : ""}${notif.address || "댓글이 달렸어요"}`
            : isCommunity
            ? notif.communityTitle || "새로운 글이 올라왔어요."
            : ""}
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: C.sub }}>
          {formatDateTime(notif.createDate)}
        </div>
      </div>
    </button>
  );
}

const ITEMS_PER_PAGE = 10;
const PAGE_GROUP = 5;

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const startPage = Math.floor((page - 1) / PAGE_GROUP) * PAGE_GROUP + 1;
  const endPage = Math.min(startPage + PAGE_GROUP - 1, totalPages);
  const numbers = [];
  for (let i = startPage; i <= endPage; i++) numbers.push(i);
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    minWidth: 34,
    height: 34,
    padding: "0 10px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    color: C.sub,
  };
  return (
    <div
      style={{
        marginTop: 8,
        display: "flex",
        gap: 8,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        onClick={() => canPrev && onChange(page - 1)}
        disabled={!canPrev}
        style={{ ...btnBase, color: canPrev ? C.body : "#D1D5DB", cursor: canPrev ? "pointer" : "default" }}
      >
        ‹ 이전
      </button>
      {numbers.map((n) => {
        const active = n === page;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            style={{
              ...btnBase,
              width: 34,
              borderRadius: 9999,
              background: active ? C.greenDark : "transparent",
              color: active ? "#fff" : C.body,
              fontWeight: active ? 800 : 600,
            }}
          >
            {n}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => canNext && onChange(page + 1)}
        disabled={!canNext}
        style={{ ...btnBase, color: canNext ? C.body : "#D1D5DB", cursor: canNext ? "pointer" : "default" }}
      >
        다음 ›
      </button>
    </div>
  );
}

export default function NotificationsScreen({ isMobile }) {
  const { userData } = useUser();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const userId = userData?.id;
  const userType = userData?.userType;

  const load = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const data = await getNotificationsApi(userId);
    setList(data);
    setLoading(false);
    // 페이지 방문 시 자동 읽음 처리
    await checkAllNotificationsApi(userId);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // 더 이상 사용하지 않는 type='작성' 옛 데이터는 숨김
  const visible = useMemo(
    () => list.filter((n) => !HIDDEN_NOTIF_TYPES.has(n.type)),
    [list],
  );
  const totalPages = Math.max(1, Math.ceil(visible.length / ITEMS_PER_PAGE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const currentItems = useMemo(
    () => visible.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [visible, page],
  );

  const handleClick = (notif) => {
    navigate(notificationTarget(notif, userType));
  };

  const handlePageChange = (p) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      style={{
        fontFamily: FONT,
        background: C.bg,
        minHeight: isMobile ? "100%" : "calc(100vh - 72px)",
      }}
    >
      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: isMobile ? "24px 16px 60px" : "32px 20px 80px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: isMobile ? 22 : 26,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            알림
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            받은 알림을 한 번에 확인하고, 항목을 누르면 해당 화면으로 이동해요.
          </div>
        </div>

        {loading ? (
          <>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: `1px solid ${C.line}`,
                  borderRadius: 14,
                  padding: 16,
                  display: "flex",
                  gap: 14,
                }}
              >
                <div style={{ width: 44, height: 44, borderRadius: 9999, background: "#F3F4F6" }} />
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ height: 14, width: "40%", background: "#F3F4F6", borderRadius: 6 }} />
                  <div style={{ height: 12, width: "70%", background: "#F3F4F6", borderRadius: 6 }} />
                </div>
              </div>
            ))}
          </>
        ) : visible.length === 0 ? (
          <div
            style={{
              padding: "60px 16px",
              background: "#fff",
              border: `1px dashed ${C.line}`,
              borderRadius: 16,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              color: C.sub,
              fontSize: 14,
            }}
          >
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 9999,
                background: C.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 30,
              }}
              aria-hidden
            >
              🔔
            </div>
            <div style={{ fontWeight: 700, color: C.ink, fontSize: 15 }}>
              아직 받은 알림이 없어요
            </div>
            <div style={{ fontSize: 12 }}>
              매칭 또는 댓글이 들어오면 여기로 알려드립니다.
            </div>
          </div>
        ) : (
          <>
            {currentItems.map((n) => (
              <NotifItem
                key={n.docId}
                notif={n}
                onClick={() => handleClick(n)}
              />
            ))}
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          </>
        )}
      </div>
    </div>
  );
}
